import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { Box, Theme, Typography, lighten, makeStyles, useTheme } from "@material-ui/core";
import { Paper, PaperBorderType, PreviewBackdrop } from "@roc/ui";
import clsx from "clsx";
import { useDocumentLibraryRoutes } from "../../hooks/useDocumentLibraryRoutes";
import { useEffect, useRef, useState } from "react";
import jsPDF from "jspdf";
import { LoginLogoImg, Page } from "@roc/feature-app-core";
import DocumentIndex from "../../components/documentIndex";

const useStyles = makeStyles((theme: Theme) => ({
  documentCardTitle: {
    justifyContent: 'center',
    textAlign: 'center',
    position: "relative",
    marginRight: "4px",
    marginLeft: "4px",
    padding: "4px",
    top: "4px",
    background: lighten(theme.palette.primary.main, 0.5),
    fontWeight: 'bold',
    border: '0.25px solid black'
  },
  documentCard: {
    padding: "0!important"
  },
  documentContainer: {
    overflowX: 'auto',
    position: 'relative',
    height: '100%',
    padding: '16px',
    background: '#FFF',
    fontSize: 16,
    '& table': {
      width: '100%',
      textAlign: 'center',
      borderCollapse: "collapse",
    },
    '& table th': {
      padding: '5px',
      background: lighten(theme.palette.primary.main, 0.5),
      border: '0.25px solid black',
    },
    '& table td': {
      padding: '5px',
      borderCollapse: "collapse",
      border: '0.25px solid black',
      textAlign: 'left',
    },
    '& td p': {
      marginLeft: '5px'
    },
    '& ul,ol': {
      marginLeft: theme.spacing(3),
      marginBottom: theme.spacing(0.5),
    },
    '& h6': {
      fontSize: 16,
      padding: 4,
      fontWeight: 'bold',
    },
    '@media (max-width: 600px)': {
      fontSize: '8px',
      padding: 0,
    },
  },

}))

const usePrintModeStyles = makeStyles((theme: Theme) => ({
  documentContainer: {
    fontSize: 10,
    '& ul,ol': {
      marginLeft: theme.spacing(2),
    },
    '& h6': {
      fontSize: 12,
    },
  },
}));

const DocumentCard = ({
  title = undefined,
  data,
  paperBorder = PaperBorderType.SOLID_BORDER,
  paperClassName = '',
  paperTitleClassName = '',
  paperBodyClassName = '',
  paperStyle = {},
}) => {
  const classes = useStyles();
  return (
    <Paper
      title={title}
      titleClass={clsx(classes.documentCardTitle, paperTitleClassName)}
      titleVariant={'h6'}
      borderType={paperBorder}
      className={clsx(classes.documentCard, paperClassName)}
      style={paperStyle}
    >
      <Box p={0.5} className={paperBodyClassName}>
        {documentToReactComponents(data)}
      </Box>
    </Paper>
  )
}

const CustomDocumentCard = ({
  primaryTitle = undefined,
  secondaryTitle = undefined,
  data,
  paperBorder = PaperBorderType.SOLID_BORDER,
  paperClassName = '',
  paperTitleClassName = '',
  paperBodyClassName = '',
  paperStyle = {},
}) => {
  const classes = useStyles();
  return <Paper
    title={primaryTitle}
    titleClass={clsx(classes.documentCardTitle, paperTitleClassName)}
    titleVariant={'h6'}
    borderType={paperBorder}
    className={clsx(classes.documentCard, paperClassName)}
    style={paperStyle}>
    <Paper
      borderType={paperBorder}
      style={{ padding: '16px' }}
    >
      {documentToReactComponents(secondaryTitle)}
    </Paper>
    <Box p={0.5} className={paperBodyClassName}>
      <DocumentCard
        data={data}
        paperBorder={paperBorder}
      />
    </Box>
  </Paper>
}

export const RentalProductManual = ({
  contentId,
  data,
  preview = false,
  printMode = false,
  setContainerRef,
}:{
  contentId: string,
  data: any,
  preview?: boolean,
  printMode?: boolean,
  setContainerRef: any,
}) => {
  const theme = useTheme();
  const { documentLibraryRoutesConfig } = useDocumentLibraryRoutes();
  const classes = useStyles();
  const printModeClasses = usePrintModeStyles();
  const containerRef = useRef(null);
  const [printModeContainerStyle, setPrintModeContainerStyle] = useState<any>();
  const [containerStyle, setContainerStyle] = useState<any>();

  const sections = [
    { id: 'keyRatios', title: data.keyRatiosTitle },
    { id: 'generalInformation', title: data.generalInformationTitle },
    { id: 'borrowerAndGuarantor', title: data.borrowerAndGuarantorTitle },
    { id: 'propertyAndSourcing', title: data.propertyAndSourcingTitle },
    { id: 'propertyFaqScenarios', title: data.propertyFaqScenariosTitle },
    { id: 'sourcingFaqScenarios', title: data.sourcingFaqScenariosTitle },
    { id: 'documentRequirements', title: data.documentRequirementsTitle },
    { id: 'borrowerExposureMonitoringSurveil', title: data.borrowerExposureMonitoringsurveilTitle },
    { id: 'glossary', title: data.glossaryTitle }
  ];

  useEffect(() => {
    const doc = new jsPDF("p", "mm", "a4");
    let width: any = doc.internal.pageSize.getWidth();
    let height: any = doc.internal.pageSize.getHeight();
    width = `${Math.floor(width)}mm`;
    height = `${Math.floor(height)}mm`;
    setPrintModeContainerStyle({
      margin: 'auto',
      minWidth: width,
      maxWidth: width,
      height: height
    });
    setContainerStyle({
      margin: 'auto',
      maxWidth: theme.breakpoints.values.lg,
    });
  }, []);

  useEffect(() => {
    setContainerRef(containerRef);
  }, [containerRef])

  if (!data) {
    return null;
  }

  return (
    <div ref={containerRef} style={printMode ? printModeContainerStyle : containerStyle}>
        <Page routeDefinition={documentLibraryRoutesConfig.documentLibraryDocument(contentId)}>
        <Box className={clsx(classes.documentContainer, printMode ? printModeClasses.documentContainer : undefined)}>
          {preview && <PreviewBackdrop />}
          <LoginLogoImg imgStyle={{ maxWidth: 200 }} />
          <Box my={2} textAlign='center'>
            <Typography variant={'h4'}>
              <strong>{data.productBox}</strong>
            </Typography>
            <Paper
              title={data.fixAndFlip}
              titleClass={classes.documentCardTitle}
              titleVariant={'h5'}
              borderType={PaperBorderType.SOLID_BORDER}
              className={classes.documentCard}
            />
            <Paper
              title={data.investmentPropertyLoan}
              titleClass={classes.documentCardTitle}
              titleVariant={'h6'}
              borderType={PaperBorderType.SOLID_BORDER}
              className={classes.documentCard}
            />
          </Box>
          <Box mb={2} paddingBottom="5%">
            <Paper
              title="Table of Contents"
              titleClass={classes.documentCardTitle}
              titleVariant={"h6"}
              borderType={PaperBorderType.SOLID_BORDER}
              className={classes.documentCard}
            >
              <DocumentIndex data={data} sections={sections} />
            </Paper>
          </Box>
          <Box id='keyRatios' mb={2} paddingBottom="5%">
            <DocumentCard
              title={data.keyRatiosTitle}
              data={data.keyRatios}
              paperBorder={PaperBorderType.NO_BORDER}
            />
          </Box>
          <Box id='generalInformation' mb={2} paddingBottom="5%">
            <DocumentCard
              title={data.generalInformationTitle}
              data={data.generalInformation}
              paperBorder={PaperBorderType.NO_BORDER}
            />
          </Box>
          <Box id='borrowerAndGuarantor' mb={2} paddingBottom="5%">
            <DocumentCard
              title={data.borrowerAndGuarantorTitle}
              data={data.borrowerAndGuarantor}
              paperBorder={PaperBorderType.NO_BORDER}
            />
          </Box>
          <Box id='propertyAndSourcing' mb={2} paddingBottom="5%">
            <DocumentCard
              title={data.propertyAndSourcingTitle}
              data={data.propertyAndSourcing}
              paperBorder={PaperBorderType.NO_BORDER}
            />
          </Box>
          <Box id='propertyFaqScenarios' mb={2} paddingBottom="5%">
            <DocumentCard
              title={data.propertyFaqScenariosTitle}
              data={data.propertyFaqScenarios}
              paperBorder={PaperBorderType.NO_BORDER}
            />
          </Box>
          <Box id='sourcingFaqScenarios' mb={2} paddingBottom="5%">
            <DocumentCard
              title={data.sourcingFaqScenariosTitle}
              data={data.sourcingFaqScenarios}
              paperBorder={PaperBorderType.NO_BORDER}
            />
          </Box>
          <Box id="documentRequirements" mb={2} paddingBottom="5%">
            <DocumentCard
              title={data.documentRequirementsTitle}
              data={data.documentRequirements}
              paperBorder={PaperBorderType.NO_BORDER}
            />
          </Box>
          <Box id='borrowerExposureMonitoringSurveil' mb={2} paddingBottom="5%">
            <DocumentCard
              title={data.borrowerExposureMonitoringsurveilTitle}
              data={data.borrowerExposureMonitoringSurveil}
              paperBorder={PaperBorderType.NO_BORDER}
            />
          </Box>
          <Box id="glossary" mb={2} paddingBottom="5%">
            <DocumentCard
              title={data.glossaryTitle}
              data={data.glossary}
              paperBorder={PaperBorderType.NO_BORDER}
            />
          </Box>
        </Box>
      </Page>
    </div>
  )
}